import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "preact/jsx-runtime";
import { useEffect, useState, useRef } from 'react';
import { useCombobox } from 'downshift';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { mapTilerApiKey } from '../apiKey';
import DeleteButton from './DeleteButton';
const currentLanguage = document.documentElement.lang === 'de-DE' ? 'de' : 'en';
const supportsAbortController = typeof window.AbortController === 'function';
const countryTerms = zankFinderData.countries;
export default function LocationSearch({ setBounds, term, setTerm, setCountryId, }) {
    const [suggestions, setSuggestions] = useState([]);
    const debouncedTerm = useDebouncedValue(term, 300);
    const abortRef = useRef(null);
    useEffect(() => {
        var _a;
        const fetchOptions = {};
        if (supportsAbortController) {
            if (abortRef.current instanceof AbortController) {
                abortRef.current.abort();
            }
            abortRef.current = new AbortController();
            fetchOptions.signal = (_a = abortRef.current) === null || _a === void 0 ? void 0 : _a.signal;
        }
        if (debouncedTerm.length < 2) {
            setSuggestions([]);
            return;
        }
        async function fetchSuggestions() {
            try {
                let url = `https://api.maptiler.com/geocoding/${debouncedTerm}.json?key=${mapTilerApiKey}`;
                /*        url += `&bbox=${[
                          5.86631529, 47.27011137, 15.04193189, 55.09916098,
                        ].join(",")}`;*/
                // Use English results until bug is fixed
                // url += `&language=${currentLanguage}`;
                const response = await fetch(url, fetchOptions);
                if (!response.ok) {
                    throw new Error('Fehler beim Laden der Daten.');
                }
                const responseObject = await response.json();
                if (Array.isArray(responseObject === null || responseObject === void 0 ? void 0 : responseObject.features)) {
                    const filteredSuggetions = getFilteredSuggestions(responseObject.features);
                    const countries = getFilteredCountries(debouncedTerm, countryTerms);
                    setSuggestions([...countries, ...filteredSuggetions]);
                }
                else {
                    setSuggestions([]);
                    // TODO: Throw error?
                }
            }
            catch (e) {
                // Ignore aborted fetch
                if (e instanceof Error && e.name === 'AbortError') {
                    return;
                }
                console.log(e);
            }
        }
        fetchSuggestions();
        return () => {
            if (abortRef.current instanceof AbortController) {
                abortRef.current.abort();
            }
        };
    }, [debouncedTerm]);
    const handleSelection = (selection) => {
        var _a, _b;
        const bbox = (_a = selection === null || selection === void 0 ? void 0 : selection.selectedItem) === null || _a === void 0 ? void 0 : _a.bbox;
        if (bbox && bbox.length === 4) {
            setBounds([
                [bbox[1], bbox[0]],
                [bbox[3], bbox[2]],
            ]);
        }
        const termId = (_b = selection === null || selection === void 0 ? void 0 : selection.selectedItem) === null || _b === void 0 ? void 0 : _b.termId;
        if (termId) {
            setCountryId(termId);
        }
        else {
            setCountryId(0);
        }
    };
    const handleFocus = (isOpen) => {
        if (!isOpen && debouncedTerm && suggestions.length) {
            openMenu();
        }
    };
    const { isOpen, getLabelProps, getMenuProps, highlightedIndex, getItemProps, getInputProps, getComboboxProps, openMenu, reset, } = useCombobox({
        items: suggestions,
        itemToString,
        onInputValueChange: (inputData) => { var _a; return setTerm((_a = inputData.inputValue) !== null && _a !== void 0 ? _a : ''); },
        onSelectedItemChange: handleSelection,
        // onStateChange: (changes) => console.log(changes),
    });
    const deleteFunction = () => {
        reset();
        setBounds(null);
        setCountryId(0);
        setTerm('');
    };
    return (_jsxs(_Fragment, { children: [_jsx("label", Object.assign({ className: "text-primary" }, getLabelProps(), { htmlFor: "location-search", id: "downshift-0-label" }, { children: _jsx("b", { children: zankFinderStrings.locationLabel }, void 0) }), void 0), _jsxs("div", Object.assign({ className: "combobox combobox--location" }, getComboboxProps(), { children: [_jsxs("div", Object.assign({ className: "input-delete-wrapper" }, { children: [_jsx("input", Object.assign({ className: "combobox__input" }, getInputProps({ onFocus: () => handleFocus(isOpen) }), { value: term, id: "location-search" }), void 0), _jsx(DeleteButton, { label: zankFinderStrings.clearInputLabel, deleteFunction: deleteFunction, descriptionId: "downshift-0-label", hidden: term.length === 0 }, void 0)] }), void 0), _jsx("ul", Object.assign({ className: "combobox__list" }, getMenuProps(), { children: isOpen &&
                            suggestions.map((suggestion, index) => (_jsx("li", Object.assign({}, getItemProps({ item: suggestion, index }), { className: `combobox__item ${highlightedIndex === index
                                    ? 'combobox__item--highlighted'
                                    : ''}` }, { children: suggestion.place_name }), suggestion.place_name))) }), void 0)] }), void 0)] }, void 0));
}
function itemToString(item) {
    return (item === null || item === void 0 ? void 0 : item.place_name) ? item.place_name : '';
}
function getFilteredSuggestions(suggestions) {
    const excludedTypes = ['country', 'street'];
    return suggestions.filter(({ place_type: types }) => {
        for (const type of types) {
            if (excludedTypes.includes(type)) {
                return false;
            }
        }
        return true;
    });
}
function getFilteredCountries(searchTerm, countries) {
    const regEx = new RegExp(searchTerm, 'i');
    return countries.filter(({ place_name, altNames }) => {
        if (regEx.test(place_name)) {
            return true;
        }
        return altNames.some((name) => regEx.test(name));
    });
}
